import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar } from 'antd';
import { resolveUrl } from '../utils/url';
import {
  DashboardOutlined,
  AppstoreOutlined,
  FolderOutlined,
  FileTextOutlined,
  UserOutlined,
  VideoCameraOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { usePermission } from '../contexts/PermissionContext';
import { useTabStore } from '../store/tabStore';
import TabBar from './TabBar';
import ThemeToggle from './ThemeToggle';
import KeepAliveOutlet from './KeepAliveOutlet';
import HelpSidebar from './HelpSidebar';
import { NotificationButton } from './NotificationButton';
import './MainLayout.css';

const { Sider, Content, Header } = Layout;

const menuItems = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
  { key: '/employees', icon: <UserOutlined />, label: '数字人' },
  { key: '/applications', icon: <AppstoreOutlined />, label: '应用管理' },
  { key: '/agent-tasks', icon: <ClockCircleOutlined />, label: '后台任务' },
  { key: '/tickets', icon: <FileTextOutlined />, label: '工单管理' },
  { key: '/products', icon: <VideoCameraOutlined />, label: '产物管理' },
  {
    key: 'resources',
    icon: <FolderOutlined />,
    label: '资源管理',
    children: [
      { key: '/skills', label: 'Skill 技能' },
      { key: '/tools', label: 'Tool 工具' },
      { key: '/mcp-servers', label: 'MCP Server' },
      { key: '/documents', label: '文档管理' },
      { key: '/experts', label: '人工专家' },
    ],
  },
  {
    key: 'system-config',
    icon: <ToolOutlined />,
    label: '系统配置',
    children: [
      { key: '/config/system_base', label: '基础配置' },
      { key: '/config/model_provider', label: '大模型配置' },
      { key: '/config/env_vars', label: '环境变量' },
      { key: '/config/plugin', label: '插件配置' },
    ],
  },
  {
    key: 'system',
    icon: <SettingOutlined />,
    label: '系统管理',
    children: [
      { key: '/user-management', label: '用户管理' },
      { key: '/role-management', label: '角色管理' },
      { key: '/dictionary-management', label: '字典管理' },
      { key: '/feature-config', label: '功能配置' },
      { key: '/help-config', label: '帮助配置' },
      { key: '/prompt-management', label: '提示词管理' },
    ],
  },
];

const ROUTE_LABEL_MAP: Record<string, string> = {
  '/dashboard': '仪表盘',
  '/applications': '应用管理',
  '/session-management': '会话管理',
  '/employees': '数字人',
  '/skills': 'Skill 技能',
  '/tools': 'Tool 工具',
  '/mcp-servers': 'MCP Server',
  '/documents': '文档管理',
  '/experts': '人工专家',
  '/tickets': '工单管理',
  '/agent-tasks': '后台任务',
  '/products': '产物管理',
  '/user-management': '用户管理',
  '/role-management': '角色管理',
  '/dictionary-management': '字典管理',
  '/feature-config': '功能配置',
  '/help-config': '帮助配置',
  '/config/model_provider': '大模型配置',
  '/config/system_base': '基础配置',
  '/config/plugin': '插件配置',
  '/config/env_vars': '环境变量',
  '/prompt-management': '提示词管理',
  '/profile': '个人中心',
};

const getLabel = (pathname: string): string => {
  if (ROUTE_LABEL_MAP[pathname]) return ROUTE_LABEL_MAP[pathname];
  if (/^\/app\/[^/]+\/view/.test(pathname)) return '应用访问';
  if (/^\/app\/[^/]+\/info/.test(pathname)) return '应用详情';
  if (/^\/employees\/\d+/.test(pathname)) return '员工详情';
  if (/^\/skills\/\d+/.test(pathname)) return 'Skill 详情';
  if (/^\/tools\/\d+/.test(pathname)) return 'Tool 详情';
  if (/^\/documents\/\d+/.test(pathname)) return '文档详情';
  if (/^\/user-management\/\d+/.test(pathname)) return '用户详情';
  return pathname;
};

const MENU_PERMISSION_MAP: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/applications': 'applications',
  '/employees': 'employees',
  '/skills': 'skills',
  '/tools': 'tools',
  '/mcp-servers': 'mcp-servers',
  '/documents': 'documents',
  '/tickets': 'tickets',
  '/products': 'products',
  '/user-management': 'user-management',
  '/role-management': 'role-management',
  '/dictionary-management': 'dictionary-management',
  '/config/model_provider': 'system-config',
  '/config/system_base': 'system-config',
  '/config/plugin': 'system-config',
  '/config/env_vars': 'system-config',
  '/prompt-management': 'agent-config',
};

// Resolve a pathname to the matching menu key (handles detail pages like /employees/10 → /employees)
const getSelectedMenuKey = (pathname: string): string => {
  // Special mappings for routes that don't share a prefix with their parent menu
  if (/^\/app\/[^/]+\/(info|view)/.test(pathname)) return '/applications';
  if (/^\/employees\/\d+\/chat/.test(pathname)) return '/employees';

  // Collect all leaf menu keys
  const leafKeys: string[] = [];
  for (const item of menuItems) {
    if ((item as any).children) {
      for (const child of (item as any).children) {
        leafKeys.push(child.key);
      }
    } else {
      leafKeys.push(item.key);
    }
  }
  // Exact match first
  if (leafKeys.includes(pathname)) return pathname;
  // Prefix match: find the longest menu key that is a prefix of the current path
  // e.g. /employees/10 matches /employees, /skills/5 matches /skills
  const matched = leafKeys
    .filter((key) => pathname.startsWith(key + '/') || pathname === key)
    .sort((a, b) => b.length - a.length);
  return matched[0] || pathname;
};

// Given a pathname, find which parent submenu keys should be open
const getOpenKeys = (pathname: string): string[] => {
  const openKeys: string[] = [];
  const selectedKey = getSelectedMenuKey(pathname);
  for (const item of menuItems) {
    if ((item as any).children) {
      const children = (item as any).children as { key: string }[];
      if (children.some((child) => selectedKey === child.key)) {
        openKeys.push(item.key);
      }
    }
  }
  return openKeys;
};

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { appName } = useAppConfig();
  const { hasMenu } = usePermission();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siderWidth, setSiderWidth] = useState(240);
  const [openKeys, setOpenKeys] = useState<string[]>(getOpenKeys(location.pathname));
  const siderResizingRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const siderWidthRef = useRef(240);

  // Update openKeys when route changes (ensure submenu stays open)
  useEffect(() => {
    const keys = getOpenKeys(location.pathname);
    if (keys.length > 0) {
      setOpenKeys((prev) => {
        const merged = new Set([...prev, ...keys]);
        return Array.from(merged);
      });
    }
  }, [location.pathname]);

  const handleSiderResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    siderResizingRef.current = { startX: e.clientX, startWidth: siderWidthRef.current };
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!siderResizingRef.current) return;
      const delta = ev.clientX - siderResizingRef.current.startX;
      const next = Math.min(400, Math.max(160, siderResizingRef.current.startWidth + delta));
      siderWidthRef.current = next;
      setSiderWidth(next);
    };
    const onUp = () => {
      siderResizingRef.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => {
    if (/^\/app\/[^/]+\/view/.test(location.pathname)) {
      setCollapsed(true);
    }
  }, [location.pathname]);
  const { addTab, pluginFullscreen } = useTabStore();

  const isTauri = !!(window as any).__TAURI__;
  const isMobile = () => window.innerWidth <= 768;

  useEffect(() => {
    const pathname = location.pathname;
    if (pathname === '/') return;
    if (/^\/app\/[^/]+\/view/.test(pathname)) return; // handled by ApplicationView
    if (/^\/app\/[^/]+\/info/.test(pathname)) return; // handled by ApplicationDetail
    if (/^\/employees\/\d+/.test(pathname)) return;   // handled by EmployeeDetail
    if (/^\/employees\/\d+\/chat/.test(pathname)) return; // handled by Chat
    addTab({
      key: pathname,
      path: pathname,
      label: getLabel(pathname),
      closable: pathname !== '/dashboard',
    });
  }, [location.pathname]);

  const filteredMenuItems = menuItems.map((item: any) => {
    if (item.children) {
      const children = item.children.filter((child: any) => {
        const perm = MENU_PERMISSION_MAP[child.key];
        return !perm || hasMenu(perm);
      });
      if (children.length === 0) return null;
      return { ...item, children };
    }
    const perm = MENU_PERMISSION_MAP[item.key];
    if (perm && !hasMenu(perm)) return null;
    return item;
  }).filter(Boolean);

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) navigate(key);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getStoredAvatar = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').avatar || null; } catch { return null; }
  };
  const [userAvatar, setUserAvatar] = useState<string | null>(getStoredAvatar);

  useEffect(() => {
    const onStorage = () => setUserAvatar(getStoredAvatar());
    window.addEventListener('storage', onStorage);
    window.addEventListener('user-updated', onStorage);
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('user-updated', onStorage); };
  }, []);

  return (
    <Layout className={`theme-${theme} ${isTauri ? 'tauri-app' : ''}`} style={{ minHeight: '100vh' }}>
      <Header className="apple-header">
        <div className="apple-header-left">
          {!pluginFullscreen && (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                if (isMobile()) setMobileOpen((o) => !o);
                else setCollapsed((c) => !c);
              }}
              className="apple-header-collapse-btn"
            />
          )}
          <div className="apple-header-logo">{appName}</div>
        </div>
        <div className="apple-header-right">
          <Button
            type="text"
            icon={<MessageOutlined />}
            className="apple-header-icon-btn"
            onClick={() => navigate('/session-management')}
            title="会话管理"
          />
          <NotificationButton />
          <ThemeToggle />
          <Dropdown
            menu={{
              items: [
                { key: 'profile', icon: <UserOutlined />, label: '个人中心', onClick: () => navigate('/profile') },
                { type: 'divider' },
                { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout },
              ],
            }}
            trigger={['click']}
            classNames={{ root: 'apple-header-dropdown' }}
          >
            <Button type="text" className="apple-header-icon-btn" style={{ padding: 0, width: 32, height: 32 }}>
              <Avatar
                size={28}
                src={userAvatar ? resolveUrl(userAvatar) : undefined}
                icon={!userAvatar ? <UserOutlined /> : undefined}
                style={{ cursor: 'pointer' }}
              />
            </Button>
          </Dropdown>
        </div>
      </Header>
      <Layout className="apple-body">
        <Sider
          theme="light"
          className="apple-sider"
          width={collapsed ? 80 : siderWidth}
          collapsedWidth={80}
          collapsed={collapsed}
          trigger={null}
          style={pluginFullscreen ? { display: 'none' } : undefined}
        >
          <Menu
            mode="inline"
            selectedKeys={[getSelectedMenuKey(location.pathname)]}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={filteredMenuItems}
            onClick={handleMenuClick}
            className="apple-menu"
          />
          {!collapsed && (
            <div className="sider-resize-handle" onMouseDown={handleSiderResizeStart} />
          )}
        </Sider>
        {/* Mobile overlay sider */}
        {mobileOpen && (
          <div className="mobile-sider-overlay" onClick={() => setMobileOpen(false)} />
        )}
        <div className={`mobile-sider${mobileOpen ? ' mobile-sider--open' : ''}`}>
          <Menu
            mode="inline"
            selectedKeys={[getSelectedMenuKey(location.pathname)]}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={filteredMenuItems}
            onClick={(info) => { handleMenuClick(info); setMobileOpen(false); }}
            className="apple-menu"
          />
        </div>
        <Layout className="apple-main">
          {!pluginFullscreen && <TabBar />}
          <Content className="apple-content">
            <KeepAliveOutlet />
          </Content>
        </Layout>
      </Layout>
      <HelpSidebar />
    </Layout>
  );
};

export default MainLayout;
